// Structured product data — ready for a future eCommerce upgrade.
// IMPORTANT: every field under `verified` is intentionally null until the owner
// supplies actual labels / supplier documentation. The UI only renders verified
// values and otherwise shows "To be confirmed — please enquire".

import SEO_ROUTES from "@/data/seoRoutes.json";

const emptyVerified = () => ({
  ingredients: null,
  size: null,
  price: null,
  availability: null,
  storage: null,
  usage: null,
  safety: null,
  allergens: null,
  shelfLife: null,
  supplier: null,
});

const commerce = () => ({ sku: null, currency: "ZAR", price: null, stock: null, variants: [], purchasable: false });

const commonFaqs = (name) => [
  {
    q: `How much does ${name} cost?`,
    a: `Pricing for ${name} is currently available on request. Send us an enquiry or WhatsApp us on 065 377 8765 and we will share current prices and available sizes.`,
  },
  {
    q: `What ingredients are in Uhlumo Organics ${name}?`,
    a: "We are verifying full ingredient information against the actual product packaging and supplier details. Until it is published here, please ask us for the label information before you buy, and always read the label on your product.",
  },
  {
    q: "Do you offer delivery?",
    a: "Delivery and collection options are confirmed per order. Please contact us with your area in Cape Town or South Africa and we will confirm what is available.",
  },
  {
    q: `Is ${name} a substitute for medical care?`,
    a: "No. Our products are not intended to diagnose, treat, cure or prevent any disease. Please consult a qualified healthcare professional before using any wellness product, particularly if you are pregnant, nursing, taking medication or have a medical condition.",
  },
];

export const PRODUCTS = [
  {
    slug: "sea-moss-gel",
    path: "/sea-moss-gel",
    name: "Sea Moss Gel",
    h1: "Sea Moss Gel from Uhlumo Organics",
    category: "Gel",
    featured: true,
    eventName: "sea_moss_enquiry",
    accent: "ocean",
    shortDescription: "A gel prepared from sea moss, a type of red seaweed. Enquire for available sizes, ingredients, prices and ordering.",
    intro: [
      "Sea Moss Gel is a key product in the Uhlumo Organics range. It is a gel prepared from sea moss, a type of red seaweed.",
      "Explore Uhlumo Organics Sea Moss Gel and enquire about available sizes, ingredients, prices and ordering. We are based in Gugulethu, Cape Town.",
    ],
    whatIs: {
      heading: "What is Sea Moss Gel?",
      paragraphs: [
        "“Sea moss” is a common name used for certain types of red seaweed. Sea Moss Gel is a gel-like product prepared from sea moss.",
        "The exact ingredients, preparation details and label information for Uhlumo Organics Sea Moss Gel will be published here once they have been verified against our product packaging. Until then, we are happy to share label details when you enquire.",
      ],
    },
    seo: {
      title: "Sea Moss Gel Cape Town | Uhlumo Organics, Gugulethu",
      description: "Enquire about Uhlumo Organics Sea Moss Gel in Cape Town. A gel prepared from sea moss (red seaweed). Ask about sizes, ingredients, pricing and delivery.",
    },
    whatsappMessage: "Hello Uhlumo Organics. I would like to enquire about your Sea Moss Gel, including the available sizes, pricing and delivery.",
    enquiryCta: "Enquire About Sea Moss Gel",
    related: ["liquid-chlorophyll", "hibiscus-beetroot-juice", "shilajit-infused-honey"],
    faqs: [
      { q: "What is Uhlumo Organics Sea Moss Gel?", a: "It is a gel prepared from sea moss, a type of red seaweed, supplied by Uhlumo Organics in Cape Town. Full label information will be published once verified." },
      { q: "Where can I buy Sea Moss Gel in Cape Town?", a: "You can enquire directly with Uhlumo Organics, based in Gugulethu, Cape Town. Use the enquiry form on this page or WhatsApp us on 065 377 8765." },
      { q: "What sizes of Sea Moss Gel are available?", a: "Available sizes are confirmed on enquiry. Ask us and we will share the current sizes and prices." },
      { q: "How should Sea Moss Gel be stored?", a: "Storage instructions will be published from our verified product label. Always follow the storage and expiry information printed on your product." },
      ...commonFaqs("Sea Moss Gel"),
    ],
  },
  {
    slug: "liquid-chlorophyll",
    path: "/liquid-chlorophyll",
    name: "Liquid Chlorophyll Drops",
    h1: "Liquid Chlorophyll Drops",
    category: "Liquid drops",
    eventName: "liquid_chlorophyll_enquiry",
    accent: "leaves",
    shortDescription: "A liquid product in drop form containing chlorophyll or a chlorophyll-derived ingredient.",
    intro: [
      "Uhlumo Organics Liquid Chlorophyll Drops are a liquid wellness product containing chlorophyll or a chlorophyll-derived ingredient.",
      "Chlorophyll is the green pigment found in plants. The precise formulation, concentration and bottle size of our drops will be published once verified against the approved label.",
    ],
    whatIs: {
      heading: "What are Liquid Chlorophyll Drops?",
      paragraphs: [
        "Liquid chlorophyll products are liquids made with chlorophyll or a chlorophyll-derived ingredient, often supplied in a dropper bottle.",
        "Directions for use must come from the approved product label. We do not publish dosage suggestions on this website — please read the label on your product and ask a qualified healthcare professional if you are unsure.",
      ],
    },
    seo: {
      title: "Liquid Chlorophyll Drops South Africa | Uhlumo Organics",
      description: "Enquire about Uhlumo Organics Liquid Chlorophyll Drops, supplied from Cape Town. Ask about ingredients, bottle size, pricing and delivery in South Africa.",
    },
    whatsappMessage: "Hello Uhlumo Organics. I would like more information about your Liquid Chlorophyll Drops.",
    enquiryCta: "Enquire About Liquid Chlorophyll",
    related: ["sea-moss-gel", "hibiscus-beetroot-juice", "collagen-zinc-vitamin-c"],
    faqs: [
      { q: "Does Uhlumo Organics sell Liquid Chlorophyll Drops?", a: "Yes. Liquid Chlorophyll Drops are part of the Uhlumo Organics range. Enquire for current availability, bottle size and pricing." },
      { q: "Can I buy Liquid Chlorophyll in Cape Town?", a: "Yes. Uhlumo Organics is based in Gugulethu, Cape Town. Contact us to confirm collection or delivery options." },
      { q: "How do I use Liquid Chlorophyll Drops?", a: "Please follow the directions on the approved product label. We do not provide dosage advice on this website." },
      ...commonFaqs("Liquid Chlorophyll Drops"),
    ],
  },
  {
    slug: "shilajit-infused-honey",
    path: "/shilajit-infused-honey",
    name: "Shilajit-Infused Honey",
    h1: "Shilajit-Infused Honey",
    category: "Infused honey",
    accent: "honey",
    shortDescription: "A product combining honey and shilajit. Enquire for size, label information and pricing.",
    intro: [
      "Uhlumo Organics Shilajit-Infused Honey is a product that combines honey and shilajit.",
      "Shilajit is a natural, resin-like substance. Details about the source, preparation and composition of our product will be published once verified against packaging and supplier documentation.",
    ],
    whatIs: {
      heading: "What is Shilajit-Infused Honey?",
      paragraphs: [
        "It is honey combined with shilajit. Purity, testing and composition information will only be published where supported by supplier or laboratory documentation.",
        "Honey is not suitable for infants under 12 months. Please read the product label and consult a qualified healthcare professional if you have any questions about whether a product is suitable for you.",
      ],
    },
    seo: {
      title: "Shilajit-Infused Honey South Africa | Uhlumo Organics",
      description: "Enquire about Uhlumo Organics Shilajit-Infused Honey, a product combining honey and shilajit, supplied from Cape Town. Ask about size, label details and pricing.",
    },
    whatsappMessage: "Hello Uhlumo Organics. I would like more information about your Shilajit-Infused Honey, including size, pricing and delivery.",
    enquiryCta: "Enquire About Shilajit-Infused Honey",
    related: ["sea-moss-gel", "collagen-zinc-vitamin-c", "liquid-chlorophyll"],
    faqs: [
      { q: "What is Shilajit-Infused Honey?", a: "It is a product that combines honey and shilajit. Verified composition and label information will be published once confirmed." },
      { q: "Has the shilajit been laboratory tested?", a: "We will only publish testing information where supporting documentation is available. Please ask us for current supplier information." },
      ...commonFaqs("Shilajit-Infused Honey"),
    ],
  },
  {
    slug: "collagen-zinc-vitamin-c",
    path: "/collagen-zinc-vitamin-c",
    name: "Collagen with Zinc & Vitamin C",
    fullName: "Collagen Powder with Zinc and Vitamin C",
    h1: "Collagen Powder with Zinc and Vitamin C",
    category: "Powdered supplement",
    accent: "beach",
    shortDescription: "A powdered nutritional supplement combining collagen, zinc and vitamin C.",
    intro: [
      "Uhlumo Organics Collagen Powder with Zinc and Vitamin C is a nutritional supplement combining collagen, zinc and vitamin C.",
      "The collagen source, ingredient quantities and directions for use will be published once verified against the product label.",
    ],
    whatIs: {
      heading: "What is Collagen Powder with Zinc and Vitamin C?",
      paragraphs: [
        "It is a powdered supplement that contains collagen together with zinc and vitamin C.",
        "Collagen is commonly derived from animal sources, so this product may not be suitable for vegetarians or vegans. Please ask us for the confirmed collagen source and allergen information before purchasing.",
      ],
    },
    seo: {
      title: "Collagen Powder with Zinc and Vitamin C | Uhlumo Organics",
      description: "Enquire about Uhlumo Organics Collagen Powder with Zinc and Vitamin C, supplied from Cape Town. Ask about collagen source, label information, size and pricing.",
    },
    whatsappMessage: "Hello Uhlumo Organics. I would like more information about your Collagen Powder with Zinc and Vitamin C.",
    enquiryCta: "Enquire About Collagen",
    related: ["shilajit-infused-honey", "sea-moss-gel", "hibiscus-beetroot-juice"],
    faqs: [
      { q: "What is the collagen source?", a: "The collagen source is being confirmed against supplier documentation. Collagen is commonly animal-derived, so please ask us before buying if this matters to you." },
      { q: "Is this product suitable for vegans?", a: "Not necessarily — collagen is commonly derived from animal sources. Please contact us for confirmed ingredient information." },
      ...commonFaqs("Collagen Powder with Zinc and Vitamin C"),
    ],
  },
  {
    slug: "hibiscus-beetroot-juice",
    path: "/hibiscus-beetroot-juice",
    name: "Hibiscus & Beetroot Juice",
    fullName: "Hibiscus and Beetroot Juice",
    h1: "Hibiscus and Beetroot Juice",
    category: "Beverage",
    accent: "hibiscus",
    shortDescription: "A beverage made with hibiscus and beetroot. Enquire for size, ingredients and pricing.",
    intro: [
      "Uhlumo Organics Hibiscus and Beetroot Juice is a beverage containing hibiscus and beetroot.",
      "Full ingredients, nutritional information, storage requirements and shelf life will be published once verified against the product label.",
    ],
    whatIs: {
      heading: "What is Hibiscus and Beetroot Juice?",
      paragraphs: [
        "It is a drink made with hibiscus and beetroot. Hibiscus is a flowering plant and beetroot is a root vegetable.",
        "Information about sugar content, preservatives and shelf life will only be shown once confirmed from the label. Please follow the storage and expiry information on your bottle.",
      ],
    },
    seo: {
      title: "Hibiscus and Beetroot Juice | Uhlumo Organics Cape Town",
      description: "Enquire about Uhlumo Organics Hibiscus and Beetroot Juice, a beverage made with hibiscus and beetroot, supplied from Cape Town. Ask about size, ingredients and pricing.",
    },
    whatsappMessage: "Hello Uhlumo Organics. I would like more information about your Hibiscus and Beetroot Juice, including size, pricing and delivery.",
    enquiryCta: "Enquire About Hibiscus & Beetroot Juice",
    related: ["sea-moss-gel", "liquid-chlorophyll", "shilajit-infused-honey"],
    faqs: [
      { q: "What is in Hibiscus and Beetroot Juice?", a: "It is a beverage containing hibiscus and beetroot. The full ingredient list will be published once verified against the product label." },
      { q: "Does the juice need to be refrigerated?", a: "Storage requirements will be published from the verified label. Please follow the storage instructions on your bottle." },
      ...commonFaqs("Hibiscus and Beetroot Juice"),
    ],
  },
].map((p) => ({ ...p, seo: { ...p.seo, ...(SEO_ROUTES[p.path] || {}) }, verified: emptyVerified(), commerce: commerce(), images: [] }));

export const PRODUCT_OPTIONS = [
  ...PRODUCTS.map((p) => ({ value: p.slug, label: p.name })),
  { value: "general", label: "General Enquiry" },
];

export const getProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);

export const VERIFIED_FIELDS = [
  { key: "ingredients", label: "Ingredients" },
  { key: "size", label: "Size / volume" },
  { key: "price", label: "Price" },
  { key: "availability", label: "Availability" },
  { key: "storage", label: "Storage" },
  { key: "usage", label: "Directions for use" },
  { key: "allergens", label: "Allergen information" },
  { key: "safety", label: "Safety information" },
  { key: "shelfLife", label: "Shelf life / expiry" },
];
